"use client";

import React, { useState, useEffect } from "react";
import { ChevronDown } from "lucide-react";
import { cn } from "@/utils/utils";
import EntityPickerModal, {
  BaseEntity,
  FetchEntitiesFn,
  CreateEntityFn,
} from "@/components/EntityPickerModal/EntityPickerModal";

interface ChipMultiSelectProps<T extends BaseEntity> {
  value: T[];
  onChange: (newValue: T[]) => void;
  fetchEntities: FetchEntitiesFn<T>;
  createEntity?: CreateEntityFn<T>;
  entityNameSingular?: string;
  entityNamePlural?: string;
  label: string;
  className?: string;
  renderEntityRow?: (
    entity: T,
    isSelected: boolean,
    toggle: (ent: T) => void,
  ) => React.ReactNode;
}

export function ChipMultiSelect<T extends BaseEntity>({
  value,
  onChange,
  fetchEntities,
  createEntity,
  entityNameSingular,
  entityNamePlural,
  label,
  className,
  renderEntityRow,
}: ChipMultiSelectProps<T>) {
  const [selectedEntities, setSelectedEntities] = useState<T[]>([]);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    setSelectedEntities(value);
  }, [value]);

  const handleConfirm = (newSelected: T[]) => {
    setSelectedEntities(newSelected);
    onChange(newSelected);
    setModalOpen(false);
  };

  const count = selectedEntities.length;

  return (
    <>
      <button
        className={cn(
          "inline-flex items-center justify-between w-full sm:w-auto sm:justify-start gap-2 h-9 px-3 py-1 rounded-md border shadow-sm text-sm font-medium transition-colors",
          "bg-white dark:bg-slate-700 border-gray-200 dark:border-slate-600 text-gray-600 dark:text-gray-300 hover:border-gray-300 dark:hover:border-slate-500 hover:bg-gray-50 dark:hover:bg-slate-600",
          count > 0 && "border-gray-300 dark:border-slate-500",
          className,
        )}
        onClick={() => setModalOpen(true)}
        type="button"
      >
        <span>{label}</span>
        <span className="inline-flex items-center gap-1">
          {count > 0 && (
            <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-primary text-white text-xs font-semibold">
              {count}
            </span>
          )}
          <ChevronDown className="h-4 w-4 text-gray-400 dark:text-gray-500" />
        </span>
      </button>

      <EntityPickerModal<T>
        createEntity={createEntity}
        entityNamePlural={entityNamePlural}
        entityNameSingular={entityNameSingular}
        fetchEntities={fetchEntities}
        initialSelectedEntities={selectedEntities}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onConfirm={handleConfirm}
        renderEntityRow={renderEntityRow}
      />
    </>
  );
}
